import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';

import { AssessmentFlagService } from '../../core/feedback/assessment-flag.service';
import { GLOSSARY_SOURCE, glossaryEntry } from '../../core/glossary/glossary';
import { GlossaryPageService, termCase } from '../../core/glossary/glossary-page.service';
import { BasisViewComponent } from '../basis-view/basis-view';

let nextId = 0;

/**
 * A rulebook term, marked and explained (FLOW-348, DOC-80 UI-DA-80-01…04).
 *
 * `<app-term key="confidence-band">Confidence band</app-term>` — the words stay
 * the page's; the explanation is the glossary's, the same on every page. The
 * first occurrence on a page carries a small question mark, every later one a
 * light dotted underline. Click, hover or keyboard focus opens the pop-up:
 * *What it means* always; *In this case* only where the page's assessment
 * record holds how the rule was applied here, *The record holds no detail of
 * how this was applied here* where it arose with no basis — and *Flag this* at
 * its foot. Nothing in it acts on the application. Ops pages only.
 */
@Component({
  selector: 'app-term',
  imports: [BasisViewComponent, DatePipe, NgTemplateOutlet],
  templateUrl: './term.html',
  styleUrl: './term.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Hover is a convenience for a mouse; click and keyboard focus are the way in.
  host: {
    '(mouseenter)': 'entry() && show()',
    '(mouseleave)': 'hoverOut()',
    '(focusout)': 'onFocusOut($event)',
    '(keydown.escape)': 'close()',
  },
})
export class TermComponent implements OnInit, OnDestroy {
  private readonly page = inject(GlossaryPageService);
  private readonly flags = inject(AssessmentFlagService);
  private readonly host: Element = inject(ElementRef).nativeElement;

  readonly key = input.required<string>();

  protected readonly popId = `term-pop-${++nextId}`;
  protected readonly entry = computed(() => glossaryEntry(this.key()));
  protected readonly first = computed(() => this.page.isFirst(this.key(), this.host));
  protected readonly case = computed(() => {
    const entry = this.entry();
    return entry
      ? termCase(entry, this.page.case()?.record ?? null)
      : ({ state: 'absent' } as const);
  });
  protected readonly run = computed(() => this.page.case()?.record.run ?? null);

  protected readonly open = signal(false);
  /** Opened by a click (stays until closed) rather than by passing the pointer over it. */
  private pinned = false;

  ngOnInit(): void {
    if (this.entry()) this.page.register(this.key(), this.host);
  }

  ngOnDestroy(): void {
    this.page.unregister(this.host);
  }

  protected toggle(): void {
    this.pinned = !this.pinned || !this.open();
    this.open.set(this.pinned);
  }

  protected show(): void {
    this.open.set(true);
  }

  protected hoverOut(): void {
    if (!this.pinned) this.open.set(false);
  }

  protected close(): void {
    this.pinned = false;
    this.open.set(false);
  }

  protected onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (!next || !this.host.contains(next)) this.close();
  }

  protected flag(): void {
    const entry = this.entry();
    const found = this.case();
    const pageCase = this.page.case();
    if (!entry || !pageCase) return;
    const run = pageCase.record.run;
    this.flags
      .open({
        application: pageCase.application,
        run: run?.short_id ?? null,
        as_at: run?.as_at ?? null,
        rulebook_version: run?.rulebook_version ?? '',
        prompt_version: run?.prompt_version ?? '',
        prompt_label: run?.prompt_label ?? '',
        line: `${entry.term} — In this case`,
        why: found.state === 'blocks' ? found.blocks : null,
        term: entry.term,
        term_rule: entry.rule,
        glossary_entry: `${GLOSSARY_SOURCE} · entry ${entry.version}`,
      })
      .subscribe();
  }
}
